import { type ReactNode, useRef, type ComponentType } from 'react';
import { Box, Fade, Stack, Typography } from '@mui/material';
import {
  CardsSkeleton,
  PPSkeleton,
  ProfileSkeleton,
} from '@/packages/home/components/skeleton';
import { useIaBuildContext } from '@/packages/home/context/ia-builder';

export type SkeletonType = 'cards' | 'profile' | 'pp';

interface SectionProps {
  children: ReactNode;
  description?: ReactNode;
  title?: ReactNode;
  tag?: ComponentType;
  skeletonType?: SkeletonType;
  onContentComplete?: () => void;
  id: number;
  skipIaBuilder?: boolean;
}

interface ContentProps {
  description: ReactNode;
  title: ReactNode;
  tag: ReactNode;
  children: ReactNode;
}

const Content = ({ description, title, tag, children }: ContentProps) => {
  return (
    <Stack gap={description ? 1 : 2}>
      <Stack
        direction="row"
        alignItems="flex-end"
        flexWrap="wrap"
        gap={{ xs: 1, md: 2 }}
      >
        {title && <Typography variant="h2">{title}</Typography>}
        {tag}
      </Stack>
      {description && <Typography mb={1}>{description}</Typography>}
      <Box>{children}</Box>
    </Stack>
  );
};

export const Section = ({
  children,
  description,
  title,
  tag,
  skeletonType,
  onContentComplete,
  id,
  skipIaBuilder = false,
}: SectionProps) => {
  const { visibleSectionIds, showAllSections, isAllSectionsLoaded } =
    useIaBuildContext();
  const TagElement = tag;
  const isVisible = skipIaBuilder
    ? showAllSections || isAllSectionsLoaded
    : visibleSectionIds.includes(id);

  const hasTriggered = useRef(false);

  const renderSkeleton = () => {
    const skeletons = {
      profile: <ProfileSkeleton />,
      pp: <PPSkeleton />,
      cards: <CardsSkeleton />,
    };

    return skeletons[skeletonType || 'cards'];
  };

  if (!isVisible) {
    if (showAllSections) {
      return renderSkeleton();
    }
    return null;
  }

  return (
    <Box id={`section-${id}`}>
      {showAllSections ? (
        <Content
          description={description}
          title={title}
          tag={TagElement ? <TagElement /> : undefined}
          children={children}
        />
      ) : (
        <Fade
          in={true}
          timeout={800}
          onEntered={() => {
            if (onContentComplete && !hasTriggered.current) {
              hasTriggered.current = true;
              setTimeout(() => onContentComplete(), 200);
            }
          }}
        >
          <Box>
            <Content
              description={description}
              title={title}
              tag={TagElement ? <TagElement /> : undefined}
              children={children}
            />
          </Box>
        </Fade>
      )}
    </Box>
  );
};
